import {
    Alert,
    Button,
    Card,
    Divider,
    Dropdown,
    DropdownItem,
    Icon,
    SearchInput,
    Tag,
    Text,
} from "@amzn/storm-ui";
import { trash } from "@amzn/storm-ui-icons";
import {
    useAsync,
    useDebouncedEffect,
    useUpdateEffect,
} from "@react-hookz/web";
import { lowerCase, startCase } from "lodash";
import React, { useEffect } from "react";
import * as R from "remeda";
import styled from "styled-components";
import * as uuid from "uuid";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../../api/cdbApi/cdbApi";
import {
    useSetDimensionValue,
    useUpdateStatusOnChange,
} from "../../../hooks/dimension/dimension.hooks";
import { useDimensionHydration } from "../../../store/dimension.hydration";
import {
    DimensionStatus,
    dimensionStatusStore,
} from "../../../store/dimension.status";
import { dimensionValueStore } from "../../../store/dimension.value";
import type { TargetSegmentPickerDimensionValue } from "../../../store/dimension.value";
import { DimensionTypes } from "../../../types/dimensions";
import type {
    DimensionConfiguration,
    DimensionTemplate,
} from "../../../types/dimensions";
import { isLoading, isSuccess } from "../../../utils/api.utils";
import { formatDate } from "../../../utils/date.utils";
import { LabelEndComponent } from "../../common/LabelEndComponent";
import { ComplexDimensionBox } from "../common/components";

const DimensionLabel = styled(Text)`
    font-weight: 700;
`;

const SegmentPickerContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const CriteriaLabel = styled("div")`
    display: flex;
    align-items: baseline;
    gap: 5px;
`;

const LeftButtonGrouping = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 5px;
`;

const SegmentGroupCard = styled(Card).attrs<{ inclusion?: boolean }>(
    (props) => ({
        $borderColor: props.inclusion
            ? props.theme.color.green[600]
            : props.theme.color.red[600],
    }),
)<{
    inclusion?: boolean;
    $borderColor?: string;
}>`
    border: 1px solid ${(props) => props.$borderColor};
    box-shadow: none;
`;

const AddSegmentGroupButton = styled(Button).attrs<{ inclusion?: boolean }>(
    (props) => ({
        $color: props.disabled
            ? props.theme.color.gray[600]
            : props.inclusion
              ? props.theme.color.green[600]
              : props.theme.color.red[600],
    }),
)<{
    inclusion?: boolean;
    $color?: string;
}>`
    border: 1px solid ${(props) => props.$color};
    box-shadow: none;
    color: ${(props) => props.$color} !important;
`;

const criteriaOptions = ["ANY", "ALL"];
const DEFAULT_SEGMENT_TYPE = "DSP_AUDIENCE";

export interface Segment {
    id: string;
    name: string;
    category: string;
    description: string;
    marketplaces: string[];
    positiveTargetGroups: string[] | null;
    negativeTargetGroups: string[] | null;
    creationTime: number;
    accessStatus: string | null;
    dataProviderId: string | null;
    channel: string | null;
}

export interface TargetSegmentPickerDimensionConfiguration
    extends DimensionConfiguration {
    label: string;
    rodeoIdDimension: string;
    marketplaceIdDimension: string;
}

export interface TargetSegmentPickerDimensionProps extends DimensionTemplate {
    type: DimensionTypes.TARGET_SEGMENT_PICKER;
    configuration: TargetSegmentPickerDimensionConfiguration;
}

const CriteriaSelector = (props: {
    inclusion?: boolean;
    groupLabel: string;
    matchCriteria: string;
    setMatchCriteria: (matchCriteria: string) => void;
}) => {
    const {
        matchCriteria,
        setMatchCriteria,
        groupLabel,
        inclusion = true,
    } = props;
    return (
        <CriteriaLabel>
            <Text inline>
                {inclusion ? "Include" : "Exclude"} customers that match
            </Text>
            <Dropdown
                inline
                hideLabel
                transparentButton
                selectedValue={matchCriteria}
                onChange={setMatchCriteria}
            >
                {criteriaOptions.map((e) => (
                    <DropdownItem key={e} value={e}>
                        {e}
                    </DropdownItem>
                ))}
            </Dropdown>
            <Text inline>of these {groupLabel}</Text>
        </CriteriaLabel>
    );
};

interface SegmentStructure {
    segmentId: string | undefined;
    segmentType: string | undefined;
    segmentSubType: string | undefined;
}

export interface SegmentGroupStructure {
    inclusion: boolean;
    matchCriteria: string;
    segments: Record<string, SegmentStructure>;
}

const SegmentGroup = (props: {
    state: SegmentGroupStructure;
    setSegmentGroup: (segmentGroup: SegmentGroupStructure) => void;
    segmentTypes: string[];
    removeSegmentGroupHandler: () => void;
    advertiserId: string;
    marketplaceId: string;
}) => {
    const {
        state,
        removeSegmentGroupHandler,
        setSegmentGroup,
        segmentTypes,
        advertiserId,
        marketplaceId,
    } = props;
    const { inclusion, segments, matchCriteria } = state;

    // handlers
    const setSegments = React.useCallback(
        (segment: Record<string, SegmentStructure>) => {
            setSegmentGroup({
                ...state,
                segments: {
                    ...segment,
                },
            });
        },
        [state],
    );

    const addSegmentHandler = React.useCallback(() => {
        setSegments({
            ...segments,
            [uuid.v4()]: {
                segmentId: undefined,
                segmentSubType: undefined,
                segmentType: DEFAULT_SEGMENT_TYPE,
            },
        });
    }, [segments, state]);

    const removeSegmentByUuid = React.useCallback(
        (id: string) => {
            setSegments(R.omit(segments, [id]));
        },
        [segments, state],
    );

    const setSegmentIdByUuid = React.useCallback(
        (id: string, segmentId: string | undefined) => {
            setSegments({
                ...segments,
                [id]: {
                    ...segments[id],
                    segmentId,
                },
            });
        },
        [segments, state],
    );

    const setSegmentTypeByUuid = React.useCallback(
        (id: string, segmentSubType: string | undefined) => {
            setSegments({
                ...segments,
                [id]: {
                    segmentSubType,
                    segmentId: undefined,
                    segmentType: DEFAULT_SEGMENT_TYPE,
                },
            });
        },
        [segments, state],
    );

    const setMatchCriteria = React.useCallback(
        (matchCriteria: string) => {
            setSegmentGroup({
                ...state,
                matchCriteria,
            });
        },
        [state],
    );

    return (
        <SegmentGroupCard inclusion={inclusion}>
            <SegmentPickerContent style={{ overflow: "auto" }}>
                <CriteriaSelector
                    matchCriteria={matchCriteria}
                    setMatchCriteria={setMatchCriteria}
                    groupLabel="segments"
                    inclusion={inclusion}
                />
                {Object.entries(segments).map(([s_id, s]) => (
                    <Segment
                        key={s_id}
                        deleteHandler={() => removeSegmentByUuid(s_id)}
                        uuid={s_id}
                        segmentId={s.segmentId}
                        setSegmentId={(segmentId) =>
                            setSegmentIdByUuid(s_id, segmentId)
                        }
                        segmentSubType={s.segmentSubType}
                        setSegmentType={(segmentSubType) =>
                            setSegmentTypeByUuid(s_id, segmentSubType)
                        }
                        segmentTypes={segmentTypes}
                        advertiserId={advertiserId}
                        marketplaceId={marketplaceId}
                    />
                ))}
                <LeftButtonGrouping>
                    <Button
                        variant="transparent"
                        small
                        onClick={removeSegmentGroupHandler}
                    >
                        Delete Group
                    </Button>
                    <Button variant="primary" small onClick={addSegmentHandler}>
                        Add Segment
                    </Button>
                </LeftButtonGrouping>
            </SegmentPickerContent>
        </SegmentGroupCard>
    );
};

const Segment = (props: {
    uuid: string;
    deleteHandler: () => void;
    segmentTypes: string[];
    segmentId: string | undefined;
    setSegmentId: (a: string | undefined) => void;
    segmentSubType: string | undefined;
    setSegmentType: (a: string | undefined) => void;
    marketplaceId: string;
    advertiserId: string;
}) => {
    const {
        deleteHandler,
        segmentTypes,
        segmentId,
        setSegmentId,
        segmentSubType,
        setSegmentType,
        uuid,
        marketplaceId,
        advertiserId,
    } = props;
    const [segmentIdDropdownSearch, setSegmentIdDropdownSearch] =
        React.useState<string | undefined>(undefined);

    const [segmentDataState, segmentDataAction] = useAsync(async () => {
        const { data } = await (
            await getCdbApiClient()
        ).getNonEndemicTargetSegments({
            advertiserId,
            marketplaceId,
            targetSegmentType: segmentSubType,
            pattern: R.isTruthy(segmentIdDropdownSearch)
                ? segmentIdDropdownSearch
                : undefined,
        });

        if (R.isNullish(data)) {
            return [];
        }

        return data as unknown as Segment[];
    }, []);

    useDebouncedEffect(
        () => {
            if (R.isNonNullish(segmentSubType)) {
                void segmentDataAction.execute();
            }
        },
        [segmentIdDropdownSearch],
        300,
    );

    useEffect(() => {
        if (R.isNonNullish(segmentSubType)) {
            if (segmentId) {
                segmentDataAction.reset();
                setSegmentIdDropdownSearch(undefined);
                setSegmentId(undefined);
            }
            void segmentDataAction.execute();
        }
    }, [segmentSubType]);

    return (
        <CriteriaLabel>
            <Button
                variant="transparent"
                small
                onClick={deleteHandler}
                data-testid="segment-delete-icon"
            >
                <Icon type={trash} style={{ color: "red" }} />
            </Button>
            <Dropdown
                inline
                hideLabel
                selectedValue={segmentSubType}
                onChange={setSegmentType}
                onOverrideLabel={(item, value) =>
                    value ? item : "Select Segment Type"
                }
            >
                {segmentTypes.map((e) => (
                    <DropdownItem key={e} value={e}>
                        {startCase(lowerCase(e))}
                    </DropdownItem>
                ))}
            </Dropdown>
            <Dropdown
                inline
                hideLabel
                disabled={R.isNullish(segmentSubType)}
                selectedValue={segmentId}
                onChange={setSegmentId}
                onOverrideLabel={(item: any, value) =>
                    value
                        ? segmentDataState.result.find(
                              (e) => e.id === segmentId,
                          )?.name
                        : "Select Segment"
                }
                preRender={() => (
                    <div style={{ padding: "3px 7px", paddingBottom: 7 }}>
                        <SearchInput
                            id={`${uuid}-search`}
                            placeholder="Search Segments"
                            fullWidth
                            onClear={() => {
                                setSegmentIdDropdownSearch("");
                            }}
                            value={segmentIdDropdownSearch}
                            onChange={(e) =>
                                setSegmentIdDropdownSearch(e.target.value)
                            }
                        />
                    </div>
                )}
            >
                {isLoading(segmentDataState.status) ? (
                    <DropdownItem disabled value="loading">
                        loading
                    </DropdownItem>
                ) : isSuccess(segmentDataState.status) &&
                  segmentDataState.result.length === 0 ? (
                    <DropdownItem disabled value="no-segments-found">
                        No segments found
                    </DropdownItem>
                ) : isSuccess(segmentDataState.status) ? (
                    segmentDataState.result.map((e) => (
                        <DropdownItem
                            key={e.id}
                            value={e.id}
                            style={{ maxWidth: 500 }}
                        >
                            <Text>
                                [{e.id}] {e.name}
                            </Text>
                            <Text size="xsmall" color="tertiary">
                                Description: {e.description}
                            </Text>
                            <Text size="xsmall" color="tertiary">
                                Creation Date:{" "}
                                {formatDate(new Date(e.creationTime))}
                            </Text>
                        </DropdownItem>
                    ))
                ) : (
                    <DropdownItem disabled value="error">
                        error
                    </DropdownItem>
                )}
            </Dropdown>
        </CriteriaLabel>
    );
};

export const TargetSegmentPickerDimension: React.FunctionComponent<
    TargetSegmentPickerDimensionProps
> = (props) => {
    const { configuration, id, optional, debug, dependencies } = props;
    const { label, rodeoIdDimension, marketplaceIdDimension } = configuration;

    // check dimension requirements
    if (R.isNullish(rodeoIdDimension)) {
        throw new Error("rodeoIdDimension not defined in configuration");
    }
    if (R.isNullish(marketplaceIdDimension)) {
        throw new Error("marketplaceIdDimension not defined in configuration");
    }

    // get dependent values from store
    const [rodeoIdDimensionValue, marketplaceIdDimensionValue] = useStore(
        dimensionValueStore,
        (state) => [
            state.getDimensionData(rodeoIdDimension),
            state.getDimensionData(marketplaceIdDimension),
        ],
    );

    const dependenciesReady = useStore(dimensionStatusStore, (state) =>
        state.checkStatusSuccess(dependencies),
    );

    // fetch segment types on marketId
    const [segmentTypesState, segmentTypesAction] = useAsync(async () => {
        const { data } = await (
            await getCdbApiClient()
        ).getNonEndemicTargetSegmentTypes({
            marketplaceId: marketplaceIdDimensionValue?.value,
        });

        if (R.isNullish(data)) {
            return [];
        }

        return data as unknown as string[];
    }, []);

    useEffect(() => {
        if (dependenciesReady && marketplaceIdDimensionValue.value) {
            void segmentTypesAction.execute();
        }
    }, [dependenciesReady, marketplaceIdDimensionValue.value]);

    // inclusion state
    const [inclusionCriteria, setInclusionCriteria] = React.useState<string>(
        criteriaOptions[0],
    );
    const [inclusionSegmentGroups, setInclusionSegmentGroups] = React.useState<
        Record<string, SegmentGroupStructure>
    >({});

    // exclusion state
    const [exclusionCriteria, setExclusionCriteria] = React.useState<string>(
        criteriaOptions[0],
    );
    const [exclusionSegmentGroups, setExclusionSegmentGroups] = React.useState<
        Record<string, SegmentGroupStructure>
    >({});

    // handlers
    const addInclusionSegmentGroup = React.useCallback(() => {
        setInclusionSegmentGroups({
            ...inclusionSegmentGroups,
            [uuid.v4()]: {
                inclusion: true,
                matchCriteria: "ANY",
                segments: {},
            },
        });
    }, [inclusionSegmentGroups]);

    const addExclusionSegmentGroup = React.useCallback(() => {
        setExclusionSegmentGroups({
            ...exclusionSegmentGroups,
            [uuid.v4()]: {
                inclusion: false,
                matchCriteria: "ANY",
                segments: {},
            },
        });
    }, [exclusionSegmentGroups]);

    const removeInclusionSegmentGroupByUuid = React.useCallback(
        (id: string) => {
            setInclusionSegmentGroups(R.omit(inclusionSegmentGroups, [id]));
        },
        [inclusionSegmentGroups],
    );

    const removeExclusionSegmentGroupByUuid = React.useCallback(
        (id: string) => {
            setExclusionSegmentGroups(R.omit(exclusionSegmentGroups, [id]));
        },
        [exclusionSegmentGroups],
    );

    const setInclusionSegmentGroup = React.useCallback(
        (id: string, segmentGroup: SegmentGroupStructure) => {
            setInclusionSegmentGroups({
                ...inclusionSegmentGroups,
                [id]: segmentGroup,
            });
        },
        [inclusionSegmentGroups],
    );

    const setExclusionSegmentGroup = React.useCallback(
        (id: string, segmentGroup: SegmentGroupStructure) => {
            setExclusionSegmentGroups({
                ...exclusionSegmentGroups,
                [id]: segmentGroup,
            });
        },
        [exclusionSegmentGroups],
    );

    // dimension integration
    useUpdateStatusOnChange<TargetSegmentPickerDimensionValue>(
        id,
        ({ value }) => {
            if (optional) return DimensionStatus.SUCCESS;

            const segmentGroups = {
                ...inclusionSegmentGroups,
                ...exclusionSegmentGroups,
            };

            // locate segment groups
            if (Object.keys(segmentGroups).length === 0) {
                return DimensionStatus.EMPTY;
            }

            // locate empty objects
            for (const sg in segmentGroups) {
                if (Object.keys(segmentGroups[sg].segments).length === 0) {
                    return DimensionStatus.INVALID;
                }
                for (const s in segmentGroups[sg].segments) {
                    if (!segmentGroups[sg].segments[s].segmentId) {
                        return DimensionStatus.INVALID;
                    }
                }
            }
            if (value.length === 0) {
                return DimensionStatus.INVALID;
            }

            return DimensionStatus.SUCCESS;
        },
    );

    const [_, setDimensionData] =
        useSetDimensionValue<TargetSegmentPickerDimensionValue>(id, (v) => {
            return { [id]: v.value };
        });

    const segmentGroupRecordToList = (
        record: Record<string, SegmentGroupStructure>,
    ): any[] => {
        return Object.values(record).map((s) => ({
            matchCriteria: s.matchCriteria,
            segmentSubGroups: Object.values(s.segments).map((s) => ({
                id: s.segmentId,
                segmentSubType: s.segmentSubType,
                segmentType: s.segmentType,
            })),
        }));
    };

    const toArtifactJsonString = React.useCallback(() => {
        return JSON.stringify({
            inclusionSegmentsGroup: {
                matchCriteria: inclusionCriteria,
                segmentList: segmentGroupRecordToList(inclusionSegmentGroups),
            },
            exclusionSegmentsGroup: {
                matchCriteria: exclusionCriteria,
                segmentList: segmentGroupRecordToList(exclusionSegmentGroups),
            },
        });
    }, [
        inclusionCriteria,
        inclusionSegmentGroups,
        exclusionCriteria,
        exclusionSegmentGroups,
    ]);

    useEffect(() => {
        setDimensionData({
            type: DimensionTypes.TARGET_SEGMENT_PICKER,
            value: toArtifactJsonString(),
            inclusionSegmentsGroup: {
                matchCriteria: inclusionCriteria,
                segmentList: inclusionSegmentGroups,
            },
            exclusionSegmentsGroup: {
                matchCriteria: exclusionCriteria,
                segmentList: exclusionSegmentGroups,
            },
        });
    }, [
        inclusionCriteria,
        inclusionSegmentGroups,
        exclusionCriteria,
        exclusionSegmentGroups,
    ]);

    const [status] = useStore(dimensionStatusStore, (state) => [state[id]]);

    // should clear data when dependencies change
    useUpdateEffect(() => {
        if (Object.keys(inclusionSegmentGroups).length > 0) {
            setInclusionSegmentGroups({});
        }
        if (Object.keys(exclusionSegmentGroups).length > 0) {
            setExclusionSegmentGroups({});
        }
    }, [rodeoIdDimensionValue, marketplaceIdDimensionValue]);

    useDimensionHydration(id, dependencies, async () => {
        throw new Error("not implemented");
    });

    return (
        <ComplexDimensionBox>
            <SegmentPickerContent>
                <div>
                    <DimensionLabel inline>{label}</DimensionLabel>
                    <LabelEndComponent {...configuration} />
                </div>
                {segmentTypesState.status === "error" && (
                    <Alert variant="error">
                        <Text>
                            Error getting segment types for this marketplace{" "}
                            {marketplaceIdDimensionValue?.value}.
                        </Text>
                    </Alert>
                )}
                <SegmentPickerContent>
                    {Object.keys(inclusionSegmentGroups).length > 1 && (
                        <CriteriaSelector
                            matchCriteria={inclusionCriteria}
                            setMatchCriteria={setInclusionCriteria}
                            groupLabel="segment groups"
                        />
                    )}
                    <SegmentPickerContent>
                        {Object.entries(inclusionSegmentGroups).map(
                            ([s_id, s]) => (
                                <SegmentGroup
                                    key={s_id}
                                    state={{ ...s }}
                                    segmentTypes={segmentTypesState.result}
                                    setSegmentGroup={(segmentGroup) =>
                                        setInclusionSegmentGroup(
                                            s_id,
                                            segmentGroup,
                                        )
                                    }
                                    removeSegmentGroupHandler={() =>
                                        removeInclusionSegmentGroupByUuid(s_id)
                                    }
                                    advertiserId={rodeoIdDimensionValue.value}
                                    marketplaceId={
                                        marketplaceIdDimensionValue.value
                                    }
                                />
                            ),
                        )}
                    </SegmentPickerContent>
                    <Divider />
                    {Object.keys(exclusionSegmentGroups).length > 1 && (
                        <CriteriaSelector
                            inclusion={false}
                            matchCriteria={exclusionCriteria}
                            setMatchCriteria={setExclusionCriteria}
                            groupLabel="segment groups"
                        />
                    )}
                    <SegmentPickerContent>
                        {Object.entries(exclusionSegmentGroups).map(
                            ([s_id, s]) => (
                                <SegmentGroup
                                    key={s_id}
                                    state={{ ...s }}
                                    segmentTypes={segmentTypesState.result}
                                    setSegmentGroup={(segmentGroup) =>
                                        setExclusionSegmentGroup(
                                            s_id,
                                            segmentGroup,
                                        )
                                    }
                                    removeSegmentGroupHandler={() =>
                                        removeExclusionSegmentGroupByUuid(s_id)
                                    }
                                    advertiserId={rodeoIdDimensionValue.value}
                                    marketplaceId={
                                        marketplaceIdDimensionValue.value
                                    }
                                />
                            ),
                        )}
                    </SegmentPickerContent>
                    <LeftButtonGrouping>
                        {segmentTypesState.status === "loading" && (
                            <Icon
                                type="spinner"
                                size="small"
                                style={{ opacity: "0.5" }}
                            />
                        )}
                        <AddSegmentGroupButton
                            variant="transparent"
                            small
                            inclusion
                            onClick={addInclusionSegmentGroup}
                            disabled={!dependenciesReady}
                        >
                            Add Inclusion Segment Group
                        </AddSegmentGroupButton>
                        <AddSegmentGroupButton
                            variant="transparent"
                            small
                            onClick={addExclusionSegmentGroup}
                            disabled={!dependenciesReady}
                        >
                            Add Exclusion Segment Group
                        </AddSegmentGroupButton>
                    </LeftButtonGrouping>
                </SegmentPickerContent>
                {debug && (
                    <Card>
                        <Tag
                            variant="beta"
                            label={`${id} - target segement picker debug details`}
                        />
                        <pre>status: {status}</pre>
                        <pre>
                            segment types fetched status:{" "}
                            {segmentTypesState.status} output:{" "}
                            {JSON.stringify(segmentTypesState.result, null, 4)}
                        </pre>
                        <pre>
                            target segement picker state <br />
                            {JSON.stringify(
                                {
                                    inclusionSegmentsGroup: {
                                        matchCriteria: inclusionCriteria,
                                        segmentList: inclusionSegmentGroups,
                                    },
                                    exclusionSegmentsGroup: {
                                        matchCriteria: exclusionCriteria,
                                        segmentList: exclusionSegmentGroups,
                                    },
                                },
                                null,
                                2,
                            )}{" "}
                            <br />
                            target segment picker non stringified <br />
                            {JSON.stringify(
                                {
                                    inclusionSegmentsGroup: {
                                        matchCriteria: inclusionCriteria,
                                        segmentList: segmentGroupRecordToList(
                                            inclusionSegmentGroups,
                                        ),
                                    },
                                    exclusionSegmentsGroup: {
                                        matchCriteria: exclusionCriteria,
                                        segmentList: segmentGroupRecordToList(
                                            exclusionSegmentGroups,
                                        ),
                                    },
                                },
                                null,
                                2,
                            )}
                        </pre>
                    </Card>
                )}
            </SegmentPickerContent>
        </ComplexDimensionBox>
    );
};
